import { App } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getAntdApp } from '@/utils/antdAppBridge';
import { getApiErrorMessage } from '@/utils/handleApiError';

function isTranslationKey(value: string): boolean {
  return /^[A-Z][A-Z0-9_]+$/.test(value);
}

type ApiFeedbackRunOptions = {
  successKey?: string;
  errorKey?: string;
  successDuration?: number;
  silent?: boolean;
};

/**
 * Non-React toast for global handlers (e.g. React Query). Prefer
 * {@link useApiFeedback} inside components and hooks.
 */
export function notifyApiError(messageText: string): void {
  try {
    getAntdApp().message.error(messageText);
  } catch {
    // AntdAppBridge not mounted yet (e.g. early bootstrap or unit tests).
  }
}

export function useApiFeedback() {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const resolveMessage = useCallback(
    (messageKeyOrText: string) =>
      isTranslationKey(messageKeyOrText)
        ? t(messageKeyOrText)
        : messageKeyOrText,
    [t],
  );

  const showSuccess = useCallback(
    (messageKeyOrText: string, duration?: number) => {
      message.success(resolveMessage(messageKeyOrText), duration);
    },
    [message, resolveMessage],
  );

  const showError = useCallback(
    (messageKeyOrText: string, duration?: number) => {
      message.error(resolveMessage(messageKeyOrText), duration);
    },
    [message, resolveMessage],
  );

  const showWarning = useCallback(
    (messageKeyOrText: string, duration?: number) => {
      message.warning(resolveMessage(messageKeyOrText), duration);
    },
    [message, resolveMessage],
  );

  const showErrorFrom = useCallback(
    (error: unknown, fallbackKey = 'ERROR_MESSAGE') => {
      message.error(getApiErrorMessage(error, t(fallbackKey)));
    },
    [message, t],
  );

  const run = useCallback(
    async <T>(
      fn: () => Promise<T>,
      options?: ApiFeedbackRunOptions,
    ): Promise<T | undefined> => {
      try {
        const result = await fn();
        if (options?.successKey && !options.silent) {
          showSuccess(options.successKey, options.successDuration);
        }
        return result;
      } catch (error) {
        showErrorFrom(error, options?.errorKey ?? 'ERROR_MESSAGE');
        return undefined;
      }
    },
    [showSuccess, showErrorFrom],
  );

  return useMemo(
    () => ({
      run,
      showSuccess,
      showError,
      showWarning,
      showErrorFrom,
    }),
    [run, showSuccess, showError, showWarning, showErrorFrom],
  );
}
